import { useState, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { t } from "@lingui/core/macro";
import {
  COGNITO_SPECIAL_CHARS,
  cognitoSpecialCharRegex,
  passwordSchema,
} from "@/lib/auth/password-schema";
import { userApi } from "@/lib/api/services/user.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { chipToast } from "@/components/ui/chip-toast";

// ── Schema ─────────────────────────────────────────────────────────────────

const schema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: t`Passwords do not match`,
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

// ── Password generator ──────────────────────────────────────────────────────

const LOWER = "abcdefghijkmnopqrstuvwxyz";
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const DIGITS = "23456789";
const SPECIAL = COGNITO_SPECIAL_CHARS;
const ALL = LOWER + UPPER + DIGITS + SPECIAL;

function generateStrongPassword(length = 16): string {
  // Guarantee at least one of each required character class.
  const pick = (set: string) => set[Math.floor(Math.random() * set.length)];
  const required = [pick(LOWER), pick(UPPER), pick(DIGITS), pick(SPECIAL)];
  const rest = Array.from({ length: length - required.length }, () => pick(ALL));
  return [...required, ...rest]
    .sort(() => Math.random() - 0.5)
    .join("");
}

// ── Component ───────────────────────────────────────────────────────────────

interface UserResetPasswordTabProps {
  userId: number;
}

export function UserResetPasswordTab({ userId }: UserResetPasswordTabProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      userApi.setPassword(String(userId), values.password),
    onSuccess: () => {
      chipToast({ message: t`Password updated successfully` });
      reset();
      setPasswordValue("");
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to set password`,
      );
    },
  });

  function handleGenerate() {
    const pw = generateStrongPassword();
    setValue("password", pw, { shouldValidate: true });
    setValue("confirmPassword", pw, { shouldValidate: true });
    setPasswordValue(pw);
    setShowPassword(true);
    setShowConfirm(true);
  }

  const onSubmit = (values: FormValues) => mutation.mutate(values);

  // ── Password strength indicator ─────────────────────────────────────────

  const checks = [
    { label: t`At least 12 characters`, pass: passwordValue.length >= 12 },
    { label: t`Lowercase letter`, pass: /[a-z]/.test(passwordValue) },
    { label: t`Uppercase letter`, pass: /[A-Z]/.test(passwordValue) },
    { label: t`Number`, pass: /\d/.test(passwordValue) },
    {
      label: t`Special character`,
      pass: cognitoSpecialCharRegex.test(passwordValue),
    },
  ];
  const passCount = checks.filter((c) => c.pass).length;
  const strengthPct = Math.round((passCount / checks.length) * 100);
  const strengthColor =
    passCount <= 2
      ? "bg-red-500"
      : passCount <= 3
        ? "bg-amber-400"
        : passCount === 4
          ? "bg-yellow-400"
          : "bg-green-500";

  return (
    <div className="flex flex-col gap-6 max-w-md">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-(--color-text-primary)">{t`Set Password`}</h2>
        <p className="text-sm text-(--color-text-secondary)">
          {t`Directly set a new permanent password for this user in Cognito.`}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {/* Password field */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="rp-password"
            className="text-sm font-medium text-(--color-text-primary)"
          >
            {t`New password`}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                id="rp-password"
                type={showPassword ? "text" : "password"}
                placeholder={t`Enter new password`}
                className="pr-10"
                {...register("password", {
                  onChange: (e: ChangeEvent<HTMLInputElement>) =>
                    setPasswordValue(e.target.value),
                })}
                disabled={mutation.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-(--color-text-secondary) hover:text-(--color-text-primary)"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              disabled={mutation.isPending}
              className="shrink-0"
            >
              <RefreshCw className="size-4 mr-1.5" />
              {t`Generate`}
            </Button>
          </div>
          {errors.password && (
            <p className="text-xs text-(--color-danger-text)">{errors.password.message}</p>
          )}
        </div>

        {/* Strength bar — only show when there's input */}
        {passwordValue.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="h-1.5 w-full rounded-full bg-(--color-surface-muted) overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${strengthColor}`}
                style={{ width: `${strengthPct}%` }}
              />
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
              {checks.map((c) => (
                <li
                  key={c.label}
                  className={`flex items-center gap-1.5 text-xs ${
                    c.pass
                      ? "text-green-600 dark:text-green-400"
                      : "text-(--color-text-secondary)"
                  }`}
                >
                  <ShieldCheck className={`size-3 shrink-0 ${c.pass ? "opacity-100" : "opacity-30"}`} />
                  {c.label}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Confirm password */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="rp-confirm"
            className="text-sm font-medium text-(--color-text-primary)"
          >
            {t`Confirm password`}
          </label>
          <div className="relative">
            <Input
              id="rp-confirm"
              type={showConfirm ? "text" : "password"}
              placeholder={t`Confirm new password`}
              className="pr-10"
              {...register("confirmPassword")}
              disabled={mutation.isPending}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-(--color-text-secondary) hover:text-(--color-text-primary)"
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs text-(--color-danger-text)">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!isValid || !isDirty || mutation.isPending}
          className="self-start"
        >
          {mutation.isPending ? t`Saving…` : t`Set password`}
        </Button>
      </form>
    </div>
  );
}
